import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// English is bundled eagerly so the very first paint is never blocked on a
// fetch. Every other language is lazy-loaded on demand via `loadLanguage()`.
import en from './locales/en.json';

/**
 * Public language catalogue — single source of truth for the language
 * switcher, the `<html lang>` attribute, RTL detection, and the lazy-load
 * map below. Native names are exactly how the language refers to itself.
 */
export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English',         flag: '🇺🇸', native: 'English' },
  { code: 'es', label: 'Spanish',         flag: '🇪🇸', native: 'Español' },
  { code: 'pt', label: 'Portuguese',      flag: '🇧🇷', native: 'Português' },
  { code: 'zh', label: 'Chinese',         flag: '🇨🇳', native: '中文' },
  { code: 'ko', label: 'Korean',          flag: '🇰🇷', native: '한국어' },
  { code: 'vi', label: 'Vietnamese',      flag: '🇻🇳', native: 'Tiếng Việt' },
  { code: 'ht', label: 'Haitian Creole',  flag: '🇭🇹', native: 'Kreyòl Ayisyen' },
  { code: 'fr', label: 'French',          flag: '🇫🇷', native: 'Français' },
  { code: 'it', label: 'Italian',         flag: '🇮🇹', native: 'Italiano' },
  { code: 'de', label: 'German',          flag: '🇩🇪', native: 'Deutsch' },
  { code: 'ru', label: 'Russian',         flag: '🇷🇺', native: 'Русский' },
  { code: 'ar', label: 'Arabic',          flag: '🇸🇦', native: 'العربية', rtl: true },
  { code: 'hi', label: 'Hindi',           flag: '🇮🇳', native: 'हिन्दी' },
  { code: 'ja', label: 'Japanese',        flag: '🇯🇵', native: '日本語' },
];

const RTL_LANGS = SUPPORTED_LANGUAGES.filter((l) => l.rtl).map((l) => l.code);
const SUPPORTED_CODES = SUPPORTED_LANGUAGES.map((l) => l.code);

// Dynamic-import map. `webpackChunkName` keeps each language in a separate
// chunk so the customer only downloads the file for the language they pick.
// English is excluded because it's bundled eagerly above.
const LOADERS = {
  es: () => import(/* webpackChunkName: "lang-es" */ './locales/es.json'),
  pt: () => import(/* webpackChunkName: "lang-pt" */ './locales/pt.json'),
  zh: () => import(/* webpackChunkName: "lang-zh" */ './locales/zh.json'),
  ko: () => import(/* webpackChunkName: "lang-ko" */ './locales/ko.json'),
  vi: () => import(/* webpackChunkName: "lang-vi" */ './locales/vi.json'),
  ht: () => import(/* webpackChunkName: "lang-ht" */ './locales/ht.json'),
  fr: () => import(/* webpackChunkName: "lang-fr" */ './locales/fr.json'),
  it: () => import(/* webpackChunkName: "lang-it" */ './locales/it.json'),
  de: () => import(/* webpackChunkName: "lang-de" */ './locales/de.json'),
  ru: () => import(/* webpackChunkName: "lang-ru" */ './locales/ru.json'),
  ar: () => import(/* webpackChunkName: "lang-ar" */ './locales/ar.json'),
  hi: () => import(/* webpackChunkName: "lang-hi" */ './locales/hi.json'),
  ja: () => import(/* webpackChunkName: "lang-ja" */ './locales/ja.json'),
};

const loaded = new Set(['en']);

/** Lazy-load a language bundle and register it with i18next. */
export async function loadLanguage(code) {
  if (!code || loaded.has(code) || !LOADERS[code]) return;
  try {
    const mod = await LOADERS[code]();
    i18n.addResourceBundle(code, 'translation', mod.default || mod, true, true);
    loaded.add(code);
  } catch (err) {
    // Fail silently to English fallback — translation files should never
    // brick the customer-facing UI even if a chunk fails to fetch.
    // eslint-disable-next-line no-console
    console.warn(`[i18n] failed to load language "${code}":`, err);
  }
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { en: { translation: en } },
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_CODES,
    nonExplicitSupportedLngs: true, // "pt-BR" → "pt", "zh-CN" → "zh"
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'nofee_lang',
      caches: ['localStorage'],
    },
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });

// Resolve "pt-BR" / "zh-CN" / etc. to our base codes so the right chunk loads.
const baseLng = (lng) => (lng || 'en').split('-')[0].toLowerCase();

/**
 * Public language setter — ensures the chunk is loaded BEFORE i18next
 * flips the active language, so the very first React re-render after the
 * switch already has the translations available (no flash of English).
 *
 * UI components should call this instead of `i18n.changeLanguage` directly.
 */
export async function setLanguage(code) {
  const base = baseLng(code);
  if (base !== 'en') await loadLanguage(base);
  await i18n.changeLanguage(base);
}

// Apply <html lang> + RTL/LTR direction whenever the language changes.
// Resource loading is now done up-front in `setLanguage`, so this handler
// only updates the document attributes.
const applyDir = (lng) => {
  const base = baseLng(lng);
  if (typeof document !== 'undefined') {
    document.documentElement.lang = base;
    document.documentElement.dir = RTL_LANGS.includes(base) ? 'rtl' : 'ltr';
  }
};

// On first load, the detector may select a non-English language; we have to
// load that chunk before React reads from i18next.
const bootstrapLng = baseLng(i18n.language || 'en');
if (bootstrapLng !== 'en') {
  // Best-effort: kick off the chunk fetch; React will re-render once it
  // arrives and i18next picks up the new resources.
  loadLanguage(bootstrapLng).then(() => {
    // Force a noop changeLanguage to make i18next re-emit and React re-render.
    i18n.changeLanguage(bootstrapLng);
  });
}
applyDir(i18n.language || 'en');
i18n.on('languageChanged', applyDir);

export default i18n;
